/**
 * Measurement — show view on the shared Inspector grammar (one-grammar law).
 * Measurements are append-only (no edit). Overview leads with the recorded value
 * (rendered prominently, the domain-specific widget) and when it was measured;
 * Relations link the parent Metric and the optional source Artifact; ids and
 * timestamps sink to a muted Technical tail; Audit closes the page.
 */
import { useShow, useOne, type HttpError } from "@refinedev/core";
import { Gauge, FileText } from "@/components/ui/icon";

import type { Measurement, Metric } from "@aos/shared";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { formatDateTime } from "@/lib/format";

/** MeasurementShow — read-only measurement detail (measurements are append-only). */
export function MeasurementShow() {
  const { query } = useShow<Measurement, HttpError>({ resource: "measurements" });
  const { data, isLoading, isError, error, refetch } = query;
  const m = data?.data;

  const { data: metricData } = useOne<Metric>({
    resource: "metrics",
    id: m?.metric_id,
    queryOptions: { enabled: Boolean(m?.metric_id) },
  });
  const unit = metricData?.data?.unit;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !m) {
    return (
      <ShowPageError
        message={error?.message ?? "Measurement not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title="Measurement"
        subtitle="An append-only metric measurement — its value, source, and time."
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Value">
            <span className="text-lg font-semibold tabular-nums text-foreground">
              {m.value}
              {unit ? (
                <span className="ml-1 text-sm font-normal text-muted-foreground">
                  {unit}
                </span>
              ) : null}
            </span>
          </Field>
          <Field label="Measured at">{formatDateTime(m.measured_at)}</Field>
          <Field label="Source system">
            {m.source_system ?? (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="Metric">
            <RefLink resource="metrics" id={m.metric_id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "metric",
                  label: "Metric",
                  icon: Gauge,
                  resource: "metrics",
                  id: m.metric_id,
                },
                {
                  key: "source-artifact",
                  label: "Source artifact",
                  icon: FileText,
                  resource: "artifacts",
                  id: m.source_artifact_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(m.id)} />
          </Field>
          <Field label="Source artifact">
            <RefLink resource="artifacts" id={m.source_artifact_id} />
          </Field>
          <Field label="Created">{formatDateTime(m.created_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="measurements" entityId={m.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default MeasurementShow;
